"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Droplet, Flame, Pipette, ShieldAlert, Sparkles, Wrench, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface MappingItem {
  id: string;
  problemTitle: string;
  problemBadge: string;
  problemSeverity: "destructive" | "default" | "secondary" | "outline";
  symptom1: string;
  symptom2: string;
  symptom3: string;
  primarySolutionTitle: string;
  primarySolutionPath: string;
  secondarySolutionTitle: string;
  secondarySolutionPath: string;
  resolutionTime: string;
  flowNote: string;
}
const mappingData: MappingItem[] = [{
  id: "clogged-drain",
  problemTitle: "Clogged Drain",
  problemBadge: "High Frequency",
  problemSeverity: "secondary",
  symptom1: "Standing water in sinks, tubs, or showers",
  symptom2: "Gurgling sounds and slow drainage",
  symptom3: "Recurring minor backups",
  primarySolutionTitle: "Drain Cleaning",
  primarySolutionPath: "/drain-cleaning/",
  secondarySolutionTitle: "",
  secondarySolutionPath: "",
  resolutionTime: "Same-Day Clearance",
  flowNote: "Motorized snaking & hydro-jetting"
}, {
  id: "water-heater-leaking",
  problemTitle: "Water Heater Leaking",
  problemBadge: "Urgent Warning",
  problemSeverity: "destructive",
  symptom1: "Puddles forming around unit base",
  symptom2: "T&P relief valve dripping or hissing",
  symptom3: "Lukewarm or rusty tinted water",
  primarySolutionTitle: "Water Heater Repair",
  primarySolutionPath: "/water-heater-repair/",
  secondarySolutionTitle: "Replacement Options",
  secondarySolutionPath: "/water-heater-replacement/",
  resolutionTime: "Rapid Diagnostics",
  flowNote: "Element, valve & tank containment"
}, {
  id: "sewer-backup",
  problemTitle: "Sewer Backup",
  problemBadge: "Biohazard Hazard",
  problemSeverity: "destructive",
  symptom1: "Foul sewage odor from floor drains",
  symptom2: "Multiple fixtures backing up simultaneously",
  symptom3: "Gurgling toilet during laundry cycles",
  primarySolutionTitle: "Sewer Line Repair",
  primarySolutionPath: "/sewer-line-repair/",
  secondarySolutionTitle: "",
  secondarySolutionPath: "",
  resolutionTime: "Priority Dispatch",
  flowNote: "Camera inspection & trenchless line fix"
}, {
  id: "burst-pipe",
  problemTitle: "Burst Pipe",
  problemBadge: "Immediate Emergency",
  problemSeverity: "destructive",
  symptom1: "Uncontrolled water spray or major ceiling pooling",
  symptom2: "Sudden complete loss of water pressure",
  symptom3: "Rushing water sound behind drywall",
  primarySolutionTitle: "Pipe Repair",
  primarySolutionPath: "/pipe-repair/",
  secondarySolutionTitle: "Emergency Plumbing",
  secondarySolutionPath: "/emergency-plumbing/",
  resolutionTime: "24/7 Rapid Response",
  flowNote: "Main shutoff triage & line reconstruction"
}, {
  id: "toilet-wont-flush",
  problemTitle: "Toilet Won't Flush",
  problemBadge: "Sanitation Priority",
  problemSeverity: "default",
  symptom1: "Water rises to rim with overflow risk",
  symptom2: "Flapper or fill valve failure in tank",
  symptom3: "Weak flush unable to evacuate bowl",
  primarySolutionTitle: "Toilet Repair",
  primarySolutionPath: "/toilet-repair/",
  secondarySolutionTitle: "",
  secondarySolutionPath: "",
  resolutionTime: "Quick Restoration",
  flowNote: "Auger clearing & mechanical rebuild"
}];
export default function ProblemMapping() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const
      }
    }
  };
  return <section id="problemtoservicemapping" data-nav="light" className="bg-background text-foreground relative w-full py-16 md:py-24 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3.5 py-1 text-xs font-semibold text-secondary-foreground mb-4">
            <Zap className="size-3.5 text-accent" />
            <span>Jonesboro Rapid Triage Guide</span>
          </div>

          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            How Plumbing Problems Connect to Professional Services
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Plumbing failures escalate quickly if misdiagnosed. Match your
            symptoms below to understand the exact repair protocol, immediate
            danger level, and direct service dispatch needed.
          </p>
        </div>

        {/* Diagnostic Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {mappingData.map((item, index) => {
          const isDualSolution = Boolean(item.secondarySolutionTitle);
          return <motion.div key={item.id} data-index={index} variants={cardVariants} className={`flex flex-col ${index === 3 ? "lg:col-span-2 xl:col-span-1" : ""}`}>
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 rounded-xl overflow-hidden group">
                  <CardHeader className="pb-3 border-b border-border/60 bg-muted/20">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <span className="text-2xs font-bold uppercase tracking-wider text-muted-foreground">
                          Diagnostic #{index + 1}
                        </span>
                        <CardTitle className="font-default text-xl font-bold text-card-foreground group-hover:text-primary transition-colors">
                          {item.problemTitle}
                        </CardTitle>
                      </div>
                      <Badge variant={item.problemSeverity} className="font-medium shrink-0">
                        {item.problemBadge}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-5 flex-1 flex flex-col justify-between gap-5">
                    {/* Problem / Symptoms Box */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        <AlertTriangle className="size-3.5 text-accent" />
                        <span>Identified Warning Signs</span>
                      </div>
                      <ul className="space-y-2 text-sm text-card-foreground">
                        <li className="flex items-start gap-2">
                          <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                          <span>{item.symptom1}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                          <span>{item.symptom2}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                          <span>{item.symptom3}</span>
                        </li>
                      </ul>
                    </div>

                    {/* Flow Connector Indicator */}
                    <div className="relative py-2">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-dashed border-border" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-background px-3 py-0.5 text-2xs font-semibold text-muted-foreground border border-border rounded-full flex items-center gap-1.5 shadow-2xs">
                          <span>Triaged Solution</span>
                          <ArrowRight className="size-3 text-primary animate-pulse" />
                        </span>
                      </div>
                    </div>

                    {/* Service & Action Resolution */}
                    <div className="rounded-lg bg-secondary/40 p-4 border border-border/80 flex flex-col justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-2xs font-bold uppercase tracking-wider text-muted-foreground">
                            Required Protocol
                          </span>
                          <span className="text-2xs font-semibold text-primary">
                            {item.resolutionTime}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {item.flowNote}
                        </p>
                      </div>

                      {/* Action Links */}
                      <div className="pt-2 flex flex-col sm:flex-row gap-2">
                        <Button asChild variant="default" size="sm" className="w-full justify-between">
                          <Link to={item.primarySolutionPath}>
                            <span className="truncate">{item.primarySolutionTitle}</span>
                            <ArrowRight className="size-3.5 ml-1" />
                          </Link>
                        </Button>

                        {isDualSolution && <Button asChild variant="outline" size="sm" className="w-full justify-between">
                            <Link to={item.secondarySolutionPath}>
                              <span className="truncate">
                                {item.secondarySolutionTitle}
                              </span>
                              <ArrowRight className="size-3.5 ml-1" />
                            </Link>
                          </Button>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Emergency Triage Banner */}
        <div className="mt-12 rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start sm:items-center gap-4">
            <div className="size-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
              <ShieldAlert className="size-6 text-primary" />
            </div>
            <div>
              <h3 className="font-default text-lg font-bold text-foreground">
                Unsure which plumbing issue you are facing?
              </h3>
              <p className="text-sm text-muted-foreground">
                Our 24/7 dispatch technicians perform instant phone triage to
                dispatch the right equipment immediately.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto">
              <Link to="/emergency-plumbing/">Request Emergency Triage</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/problems/">View All Problem Guides</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}